import { mainStore } from '~/stores';
import SessionStorage from '~/stores/SessionStorage';

const EARTH_RADIUS_METERS = 6371000;

const RN_GEOLOCATION_TIMEOUT_MS = 6000;

const RN_GEOLOCATION_MAX_ATTEMPTS = 3;

const GEOLOCATION_UNAVAILABLE_SESSION_KEY = 'geofencing_geo_unavailable';

const GEOLOCATION_OPTIONS: PositionOptions = {
  enableHighAccuracy: false,
  timeout: 5000,
  maximumAge: 60000,
};

const toRadians = (degrees: number): number => (degrees * Math.PI) / 180;

/**
 * Validates that coordinates are usable for a distance check. Rejects
 * `null`, non-finite values, out-of-range latitude/longitude, and the
 * `(0, 0)` "null island" point that broken geolocation providers return
 * instead of an error.
 * @param coordinates - coordinates to validate
 */
export const isValidCoordinates = (
  coordinates: GeoCoordinates | null | undefined,
): coordinates is GeoCoordinates => {
  if (!coordinates) {
    return false;
  }

  const { lat, lng } = coordinates;

  return (
    Number.isFinite(lat) &&
    Number.isFinite(lng) &&
    Math.abs(lat) <= 90 &&
    Math.abs(lng) <= 180 &&
    !(lat === 0 && lng === 0)
  );
};

/**
 * Calculates the great-circle distance between two geographic points
 * using the haversine formula.
 * @param a - first coordinate
 * @param b - second coordinate
 * @returns distance between `a` and `b` in meters
 */
export const getDistanceInMeters = (
  a: GeoCoordinates,
  b: GeoCoordinates,
): number => {
  const dLat = toRadians(b.lat - a.lat);
  const dLng = toRadians(b.lng - a.lng);
  const sinLat = Math.sin(dLat / 2);
  const sinLng = Math.sin(dLng / 2);

  const h =
    sinLat * sinLat +
    Math.cos(toRadians(a.lat)) * Math.cos(toRadians(b.lat)) * sinLng * sinLng;

  return 2 * EARTH_RADIUS_METERS * Math.asin(Math.sqrt(Math.min(1, h)));
};

interface RNGeolocationResponse {
  location?: {
    coords?: {
      latitude?: number;
      longitude?: number;
    };
  };
}

/**
 * Requests the device position from the native app via the RN bridge.
 * Best-effort and side-effect-free: retries up to
 * `RN_GEOLOCATION_MAX_ATTEMPTS` times only when the bridge call succeeds
 * with empty coordinates (a transient native quirk, mirroring
 * `useGeolocation`), the whole sequence bounded by
 * `RN_GEOLOCATION_TIMEOUT_MS`. A bridge error — including a denied
 * permission on the native side — settles immediately without retrying,
 * so a denial cannot surface as a burst of native permission prompts
 * (JS-14361). Resolves to `null` on exhausted attempts, timeout, bridge
 * error, or malformed payload. Permission is governed by the native layer
 * (delivery apps normally already hold location permission by checkout).
 */
const getRNCoordinates = (): Promise<GeoCoordinates | null> =>
  new Promise((resolve) => {
    let isSettled = false;
    let attempts = 0;

    const settle = (value: GeoCoordinates | null) => {
      if (isSettled) {
        return;
      }
      isSettled = true;
      clearTimeout(timeoutId);
      resolve(value);
    };

    const timeoutId = setTimeout(() => settle(null), RN_GEOLOCATION_TIMEOUT_MS);

    const requestOnce = () => {
      if (isSettled) {
        return;
      }
      attempts += 1;

      const retryOrFail = () => {
        if (attempts < RN_GEOLOCATION_MAX_ATTEMPTS) {
          requestOnce();
        } else {
          settle(null);
        }
      };

      mainStore.sendToRN(
        'getGeolocation',
        { target: 'findme_icon' },
        (data: RNGeolocationResponse) => {
          const lat = data?.location?.coords?.latitude;
          const lng = data?.location?.coords?.longitude;
          if (typeof lat === 'number' && typeof lng === 'number') {
            settle({ lat, lng });
          } else {
            retryOrFail();
          }
        },
        () => settle(null),
      );
    };

    requestOnce();
  });

/**
 * Forgets a remembered geolocation failure so the next geofencing check
 * polls the RN bridge again.
 *
 * Called whenever the OS permission could have changed since that failure:
 * the app coming back from the background (the native shell's "We can't
 * find you" alert sends the user straight to the system settings) or the
 * address modal resolving a position through the same bridge. Without it a
 * single denial would keep the geofencing notice switched off until the app
 * is restarted (JS-14361).
 */
export const forgetGeolocationUnavailable = (): void => {
  SessionStorage.remove(GEOLOCATION_UNAVAILABLE_SESSION_KEY);
};

/**
 * Reads the device's current coordinates for the geofencing check.
 *
 * Never triggers a web permission prompt: on web it inspects the current
 * `geolocation` permission state and resolves the position only if it is
 * already `granted`. On React Native it asks the native app through the
 * bridge, where the OS governs permission. Resolves to `null` for
 * `denied`/`prompt` states, missing APIs, errors, or invalid coordinates.
 *
 * On React Native, an unsuccessful result is remembered in `sessionStorage`:
 * while that flag is set the bridge is not polled again, so a denial cannot
 * repeat as a new prompt burst on a later check (address change, method
 * switch, re-entering checkout). The flag is deliberately not permanent —
 * `forgetGeolocationUnavailable` clears it once the OS permission could have
 * changed, otherwise a user who denied and then granted access would lose
 * the geofencing notice until the app is restarted.
 */
export const getCurrentCoordinatesIfGranted =
  async (): Promise<GeoCoordinates | null> => {
    if (mainStore.isRN) {
      if (SessionStorage.get(GEOLOCATION_UNAVAILABLE_SESSION_KEY)) {
        return null;
      }

      const rnCoordinates = await getRNCoordinates();

      if (!isValidCoordinates(rnCoordinates)) {
        SessionStorage.set(GEOLOCATION_UNAVAILABLE_SESSION_KEY, true);
        return null;
      }

      return rnCoordinates;
    }

    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      return null;
    }

    if (!navigator.permissions?.query) {
      return null;
    }

    try {
      const status = await navigator.permissions.query({
        name: 'geolocation',
      });

      if (status.state !== 'granted') {
        return null;
      }
    } catch {
      return null;
    }

    return new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const coordinates: GeoCoordinates = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };
          resolve(isValidCoordinates(coordinates) ? coordinates : null);
        },
        () => resolve(null),
        GEOLOCATION_OPTIONS,
      );
    });
  };
